import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MRPController = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('mrp_controllers');
  const [viewItem, setViewItem] = useState(null);
  const [mrpControllers, setMrpControllers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [businessAreas, setBusinessAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingController, setEditingController] = useState(null);

  useEffect(() => {
    loadMrpControllers();
    loadPlants();
    loadBusinessAreas();
  }, []);

  const loadMrpControllers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/mrp-controllers`);
      setMrpControllers(response.data || []);
    } catch (error) {
      console.error('Error loading MRP controllers:', error);
      toast.error('Failed to load MRP controllers');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPlants = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-management/plants`);
      setPlants(response.data || []);
    } catch (error) {
      console.error('Error loading plants:', error);
      toast.error('Failed to load plants');
    }
  };

  const loadBusinessAreas = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/finance/business-areas`);
      setBusinessAreas(response.data || []);
    } catch (error) {
      console.error('Error loading business areas:', error);
      toast.error('Failed to load business areas');
    }
  };

  const handleOpenDialog = (controller = null) => {
    setEditingController(controller);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      mrp_controller_id: (formData.get('mrp_controller_id') || '').toUpperCase(),
      plant_id: formData.get('plant_id') || '',
      business_area_id: formData.get('business_area_id') || '',
      description: formData.get('description') || ''
    };

    try {
      if (editingController) {
        await axios.put(`${BACKEND_URL}/api/mrp-controllers/${editingController.mrp_controller_id}`, {
          plant_id: data.plant_id,
          business_area_id: data.business_area_id,
          description: data.description
        });
        toast.success('MRP Controller updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/mrp-controllers`, data);
        toast.success('MRP Controller created successfully');
      }
      setIsDialogOpen(false);
      loadMrpControllers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save MRP controller');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">MRP Controllers</h1>
            <p className="text-sm text-gray-500 mt-1">Manage MRP controllers with plant and business area</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">MRP Controllers</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add MRP Controller
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Controller ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plant</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Business Area</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mrpControllers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-gray-500">
                        No MRP controllers found. Click &quot;Add MRP Controller&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    mrpControllers.map((controller) => {
                      const plant = plants.find(p => p.plant_id === controller.plant_id);
                      const businessArea = businessAreas.find(ba => ba.business_area === controller.business_area_id);
                      return (
                      <tr key={controller.mrp_controller_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{controller.mrp_controller_id}</td>
                        <td className="py-3 px-4 text-sm">{plant ? `${plant.plant_id} - ${plant.plant_name}` : controller.plant_id}</td>
                        <td className="py-3 px-4 text-sm">{businessArea ? `${businessArea.business_area} - ${businessArea.name}` : controller.business_area_id}</td>
                        <td className="py-3 px-4 text-sm">{controller.description}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="mrp_controllers" onView={() => setViewItem(controller)} onEdit={() => handleOpenDialog(controller)} />
                        </td>
                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingController ? 'Edit MRP Controller' : 'Add MRP Controller'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mrp_controller_id">MRP Controller ID * (3 digits)</Label>
                  <Input
                    id="mrp_controller_id"
                    name="mrp_controller_id"
                    defaultValue={editingController?.mrp_controller_id || ''}
                    placeholder="e.g., 001, 002, 003"
                    maxLength={3}
                    minLength={3}
                    pattern="[0-9]{3}"
                    title="Must be exactly 3 digits"
                    disabled={!!editingController}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be exactly 3 digits (e.g., 001, 002)</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingController?.description || ''}
                    placeholder="e.g., Main Production Controller"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plant *</Label>
                  <select
                    name="plant_id"
                    defaultValue={editingController?.plant_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Plant</option>
                    {plants.map(plant => (
                      <option key={plant.plant_id} value={plant.plant_id}>
                        {plant.plant_id} - {plant.plant_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Business Area *</Label>
                  <select
                    name="business_area_id"
                    defaultValue={editingController?.business_area_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Business Area</option>
                    {businessAreas.map(ba => (
                      <option key={ba.business_area} value={ba.business_area}>
                        {ba.business_area} - {ba.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingController ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default MRPController;
